import { MotionConfig } from 'framer-motion';
import React, { ReactElement } from 'react';
import { twMerge } from 'tailwind-merge';
import Footer from './partials/footer';
import Navigation from './partials/navigation';
import { BaseBgClasses, BaseColors } from '../../../types/colors';
import { FOCUS_RING_INVERSE_CONTAINER } from '../../button/variants';

interface IProps {
  children: any;
  pageBgColor?: BaseColors;
  navBgColor?: BaseColors;
  hidePrivPolicy?: boolean;
  hideNavigation?: boolean;
  hideFooter?: boolean;
  className?: string;
  style?: any;
}

/*
  Spelled out rather than interpolated as `bg-base-${navBgColor}`: Tailwind
  scans source text for whole class names, so a built-up one is never
  generated. `bg-base-transparent` was not a real class at all — two pages pass
  navBgColor="transparent" and were getting a transparent nav by accident.
*/
const NAV_BG: Record<BaseColors, BaseBgClasses> = {
  transparent: 'bg-transparent',
  'purple-500': 'bg-base-purple-500',
  'purple-400': 'bg-base-purple-400',
  'purple-300': 'bg-base-purple-300',
  'purple-200': 'bg-base-purple-200',
  'purple-100': 'bg-base-purple-100',
  blue: 'bg-base-blue',
  yellow: 'bg-base-yellow',
  pink: 'bg-base-pink',
  teal: 'bg-base-teal',
  bone: 'bg-base-bone',
  white: 'bg-base-white',
};

/*
  Nav backgrounds too dark for the default focus ring, which needs 3:1 against
  whatever sits behind it. Everything else in NAV_BG is light enough to use it.

  purple-300 and pink are the awkward middle — neither ring clears 3:1 on them
  — but neither is used as a nav background, and the only places they appear at
  all (the feature-grid cells) hold no interactive elements.
*/
const DARK_NAV_BG: Partial<Record<BaseColors, true>> = {
  'purple-500': true,
  'purple-400': true,
  blue: true,
};

// The main template that every other template/page should use.
// It contains our nav, footer, and lots of other things that are
// necessary like our cookie consent.
export default function GlobalTemplate({
  children,
  pageBgColor,
  navBgColor,
  hideNavigation,
  hideFooter,
  className = '',
  style,
}: IProps): ReactElement {
  return (
    <MotionConfig reducedMotion="user">
      {pageBgColor && (
        <style
          dangerouslySetInnerHTML={{
            __html: `
          body { background-color: var(--color-base-${pageBgColor}) }
        `,
          }}
        />
      )}

      <div
        className={twMerge('overflow-hidden', className)}
        style={{ ...style }}>
        {!hideNavigation && (
          <div
            className={twMerge(
              'flex justify-center p-4',
              navBgColor && NAV_BG[navBgColor],
              navBgColor &&
                DARK_NAV_BG[navBgColor] &&
                FOCUS_RING_INVERSE_CONTAINER
            )}>
            <div className="w-full max-w-240">
              <Navigation bgColor={navBgColor} />
            </div>
          </div>
        )}

        {children}
      </div>

      {!hideFooter && <Footer />}
    </MotionConfig>
  );
}
